import React, { useMemo } from 'react';
import {
  Checkbox,
  useTheme,
  Box,
  Stack,
  Typography,
  TextField
} from '@mui/material';
import IndeterminateCheckBoxIcon from '@mui/icons-material/IndeterminateCheckBox';
import { useFiltersByName } from 'src/hooks/useFiltersByName';
import { useUnlimitedScroll } from 'src/hooks/useUnlimitedScroll';
import InfiniteScroll from 'react-infinite-scroll-component';
import { IGroupProductEcommerceWithColor } from './EditItemComparison';
import { SketchPicker } from 'react-color';

interface TableProps {
  dataBrand: IGroupProductEcommerceWithColor[];
  setDataBrand: React.Dispatch<
    React.SetStateAction<IGroupProductEcommerceWithColor[]>
  >;
  selectedBrands: IGroupProductEcommerceWithColor[];
  setSelectedBrands: React.Dispatch<
    React.SetStateAction<IGroupProductEcommerceWithColor[]>
  >;
}

const TableBrand: React.FC<TableProps> = ({
  dataBrand,
  setDataBrand,
  selectedBrands,
  setSelectedBrands
}) => {
  const theme = useTheme();

  // Check if all products in a brand are selected
  const isBrandFullySelected = (brand: IGroupProductEcommerceWithColor) => {
    return selectedBrands.some((item) => item.id === brand.id);
  };

  // Handle brand checkbox change
  const handleBrandCheckChange = (
    brand: IGroupProductEcommerceWithColor,
    checked: boolean
  ) => {
    if (checked) {
      // Add all products from brand
      setSelectedBrands([...selectedBrands, brand]);
    } else {
      // Remove all products from brand
      setSelectedBrands(selectedBrands.filter((item) => item.id !== brand.id));
    }
  };

  const handleChangeColorBrand = (
    brand: IGroupProductEcommerceWithColor,
    color: string
  ) => {
    setDataBrand(
      dataBrand.map((item) => {
        return {
          ...item,
          color: item.id === brand.id ? color : item.color
        };
      })
    );
    setSelectedBrands(
      selectedBrands.map((item) => {
        return {
          ...item,
          color: item.id === brand.id ? color : item.color
        };
      })
    );
  };

  const handleChangeInputColorBrand = (
    brand: IGroupProductEcommerceWithColor
  ) => {
    setDataBrand(
      dataBrand.map((item) => {
        return {
          ...item,
          isInputColor:
            item.id === brand.id ? !item.isInputColor : item.isInputColor
        };
      })
    );
    setSelectedBrands(
      selectedBrands.map((item) => {
        return {
          ...item,
          isInputColor:
            item.id === brand.id ? !item.isInputColor : item.isInputColor
        };
      })
    );
  };

  // Handle select all checkbox change
  const handleSelectAllChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.checked) {
      // Select all products
      setSelectedBrands(dataBrand);
    } else {
      // Clear all selections
      setSelectedBrands([]);
    }
  };

  const addName = useMemo((): Array<IGroupProductEcommerceWithColor> => {
    return dataBrand.map((item) => ({
      ...item,
      name: item.name
    }));
  }, [dataBrand]);

  const { filteredList, handleFilter } =
    useFiltersByName<IGroupProductEcommerceWithColor>(addName);

  const { fetchMoreData, infiniteScroll } =
    useUnlimitedScroll<IGroupProductEcommerceWithColor>(filteredList);

  return (
    <>
      <Box
        sx={{
          border: `1px solid ${theme.palette.grey[300]}`,
          p: theme.spacing(1),
          borderRadius: theme.spacing(1)
        }}
      >
        <Typography fontSize="16px" fontWeight={700}>
          Brand
        </Typography>
        <Typography fontSize="14px" color={theme.palette.grey[500]}>
          Select main brand objects and set chart color.
        </Typography>
      </Box>
      <Box
        sx={{
          border: `1px solid ${theme.palette.grey[300]}`,
          p: theme.spacing(1),
          borderRadius: theme.spacing(1)
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          width="100%"
        >
          <TextField
            name="keyword"
            variant="outlined"
            size="small"
            autoComplete="off"
            placeholder="Search Brand"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                backgroundColor: 'white',
                fontSize: '14px'
              }
            }}
            onChange={(event) => handleFilter(event.target.value)}
          />
          <Stack direction="row" alignItems="center" justifyContent="center">
            <Checkbox
              checked={dataBrand.length === selectedBrands.length}
              indeterminate={
                selectedBrands.length > 0 &&
                dataBrand.length !== selectedBrands.length
              }
              size="small"
              onChange={handleSelectAllChange}
              indeterminateIcon={
                <IndeterminateCheckBoxIcon
                  sx={{
                    width: theme.spacing(2.5),
                    height: theme.spacing(2.5)
                  }}
                />
              }
            />
            <Typography fontSize="14px" color={theme.palette.grey[500]}>
              Select All Brand
            </Typography>
          </Stack>
        </Stack>
        <InfiniteScroll
          dataLength={infiniteScroll.list.length}
          next={fetchMoreData}
          hasMore={infiniteScroll.hasMore}
          loader={<div />}
          height={250}
          endMessage={<div />}
        >
          {infiniteScroll.list.map((brand) => (
            <Box
              key={brand.id}
              sx={{
                border: `1px solid ${theme.palette.grey[300]}`,
                p: theme.spacing(1),
                borderRadius: theme.spacing(1),
                my: theme.spacing(1)
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                width="100%"
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Checkbox
                    checked={isBrandFullySelected(brand)}
                    onChange={(e) =>
                      handleBrandCheckChange(brand, e.target.checked)
                    }
                  />
                  <Box
                    sx={{
                      width: '10px',
                      height: '10px',
                      backgroundColor: brand.color,
                      borderRadius: '100%'
                    }}
                  />
                  <Typography fontSize="14px" fontWeight={700}>
                    {brand.name}
                  </Typography>
                </Stack>
                {/* <Stack>
                  <Box
                    sx={{
                      border: `1px solid ${theme.palette.grey[500]}`,
                      p: theme.spacing(1),
                      borderRadius: theme.spacing(1),
                      cursor: 'pointer'
                    }}
                    onClick={() => handleChangeInputColorBrand(brand)}
                  >
                    <Box
                      sx={{
                        width: '12px',
                        height: '12px',
                        backgroundColor: brand.color,
                        borderRadius: '100%'
                      }}
                    />
                  </Box>
                  {brand.isInputColor ? (
                    <div
                      style={{
                        position: 'absolute',
                         zIndex: '5'
                      }}
                    >
                      <div
                        style={{
                          position: 'fixed',
                          top: '0px',
                          right: '0px',
                          bottom: '0px',
                          left: '0px'
                        }}
                        onClick={() => handleChangeInputColorBrand(brand)}
                      />
                      <SketchPicker
                        color={brand.color}
                        onChangeComplete={(color) =>
                          handleChangeColorBrand(brand, color.hex)
                        }
                      />
                    </div>
                  ) : null}
                </Stack> */}
              </Stack>
            </Box>
          ))}
        </InfiniteScroll>
      </Box>
    </>
  );
};

export default TableBrand;
